// @ts-check
// Composer do WhatsApp: textarea que cresce com o texto (Enter envia, Shift+Enter quebra a linha),
// anexo enviado antes por POST /rest/files e envio com fileIds.
import { html } from 'htm/preact';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import { uploadFile } from '../../data/conversations.js';
import { IconButton, LightIconButton } from '../../ui/button.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { generateId } from '../../lib/utils.js';
import { DocumentChip } from './message-attachments.js';
import { t } from '../../i18n/index.js';

const MAX_TEXTAREA_HEIGHT = 160;

/**
 * @typedef {{ localId: string, file: File, localUrl: string, fileId: string | null, isUploading: boolean }} PendingAttachment
 * @typedef {{ fileId: string, fileName: string, mimeType: string, size: number, localUrl: string }} UploadedAttachment
 */

/**
 * Sobe os arquivos escolhidos e mantém a lista de anexos pendentes do composer.
 */
export function usePendingAttachments() {
  const [attachments, setAttachments] = useState(/** @type {PendingAttachment[]} */ ([]));

  /** @param {FileList | null} fileList */
  const addFiles = (fileList) => {
    for (const file of Array.from(fileList ?? [])) {
      const pending = { localId: generateId(), file, localUrl: URL.createObjectURL(file), fileId: null, isUploading: true };
      setAttachments((current) => [...current, pending]);
      uploadFile(file)
        .then((uploaded) => {
          setAttachments((current) => current.map((item) => (item.localId === pending.localId ? { ...item, fileId: uploaded.id, isUploading: false } : item)));
        })
        .catch((error) => {
          showErrorSnackbar(error);
          setAttachments((current) => current.filter((item) => item.localId !== pending.localId));
        });
    }
  };

  /** @param {string} localId */
  const removeAttachment = (localId) => setAttachments((current) => current.filter((item) => item.localId !== localId));
  const clear = () => setAttachments([]);
  const isUploading = attachments.some((item) => item.isUploading);
  /** @returns {UploadedAttachment[]} */
  const getUploaded = () =>
    attachments
      .filter((item) => item.fileId)
      .map((item) => ({ fileId: /** @type {string} */ (item.fileId), fileName: item.file.name, mimeType: item.file.type || 'application/octet-stream', size: item.file.size, localUrl: item.localUrl }));
  return { attachments, addFiles, removeAttachment, clear, isUploading, getUploaded };
}

/**
 * @param {{ attachments: PendingAttachment[], onRemove: (localId: string) => void }} props
 */
export function PendingAttachmentList({ attachments, onRemove }) {
  if (attachments.length === 0) return null;
  return html`<div class="composer-attachments">
    ${attachments.map((item) => html`<${DocumentChip}
      key=${item.localId}
      attachment=${{ fileName: item.file.name, mimeType: item.file.type, size: item.file.size, url: '' }}
      isUploading=${item.isUploading}
      onRemove=${() => onRemove(item.localId)}
    />`)}
  </div>`;
}

/**
 * @param {{ isDisabled?: boolean, placeholder?: string, onSend: (input: { text: string, attachments: UploadedAttachment[] }) => void }} props
 */
export function MessageComposer({ isDisabled = false, placeholder, onSend }) {
  const [text, setText] = useState('');
  const textareaRef = useRef(/** @type {HTMLTextAreaElement | null} */ (null));
  const fileInputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const pending = usePendingAttachments();

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(MAX_TEXTAREA_HEIGHT, textarea.scrollHeight)}px`;
  }, [text]);

  const uploaded = pending.getUploaded();
  const canSend = !isDisabled && !pending.isUploading && (text.trim() !== '' || uploaded.length > 0);

  const submit = () => {
    if (!canSend) return;
    onSend({ text: text.trim(), attachments: uploaded });
    setText('');
    pending.clear();
    textareaRef.current?.focus();
  };

  return html`<div class="message-composer" data-disabled=${isDisabled ? 'true' : undefined}>
    <${PendingAttachmentList} attachments=${pending.attachments} onRemove=${pending.removeAttachment} />
    <div class="message-composer-row">
      <${LightIconButton}
        icon="IconPaperclip"
        size="medium"
        accent="tertiary"
        ariaLabel=${t('conversations.composer.attach')}
        disabled=${isDisabled}
        onClick=${() => fileInputRef.current?.click()}
      />
      <input
        ref=${fileInputRef}
        type="file"
        multiple
        hidden
        onChange=${(event) => {
          const input = /** @type {HTMLInputElement} */ (event.currentTarget);
          pending.addFiles(input.files);
          input.value = '';
        }}
      />
      <textarea
        ref=${textareaRef}
        class="message-composer-input"
        rows="1"
        value=${text}
        disabled=${isDisabled}
        placeholder=${placeholder ?? t('conversations.composer.placeholder')}
        aria-label=${placeholder ?? t('conversations.composer.placeholder')}
        title=${isDisabled ? undefined : t('conversations.composer.hint')}
        onInput=${(event) => setText(/** @type {HTMLTextAreaElement} */ (event.currentTarget).value)}
        onKeyDown=${(event) => {
          if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
            event.preventDefault();
            submit();
          }
        }}
      ></textarea>
      <${IconButton} icon="IconSend" accent="blue" size="medium" ariaLabel=${t('conversations.composer.send')} disabled=${!canSend} onClick=${submit} />
    </div>
  </div>`;
}
